(function () {
  'use strict';
  // Garde de double chargement : ce script est émis par plusieurs surfaces du thème
  // (alert-bikes-modal est rendu globalement sur toutes les pages, featured-collection
  // sur la homepage…). Sans cette sortie anticipée, une seconde évaluation du même
  // <script src> lève `SyntaxError: Identifier 'GreenleazeRentalOptions' has already
  // been declared`. Le premier chargement, lui, SURVIT (l'erreur est levée en
  // GlobalDeclarationInstantiation du SECOND script, après que le premier a tourné
  // entièrement) : le dommage est une erreur console permanente et le second fichier
  // entièrement mort.
  if (customElements.get('greenleaze-rental-options')) return;

class GreenleazeRentalOptions extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (window.greenleazeDebug) console.log('GreenleazeRentalOptions connected');
    this.productPrice = parseFloat(this.getAttribute('data-price')) || 0;
    this.variantId = this.getAttribute('data-variant-id') || null;
    this.scriptUrl = this.getAttribute('data-script-url') || '';

    // Récupère le discount depuis les attributs data-*
    this.discount_type = this.getAttribute('data-discount-type') || '';
    this.discount_value = parseFloat(this.getAttribute('data-discount-value')) || 0;
    this.fmdCap = parseFloat(this.getAttribute('data-fmd-cap')) || 0;
    this.reductionFmd = this.getAttribute('data-reduction-fmd') === 'true';

    // Si pas de discount en attribut, on vérifie le localStorage (parcours formulaire FMD)
    if (!this.discount_type && this.discount_value === 0) {
      const fmdData = this.getFmdDataFromSession();
      if (fmdData && fmdData.amount) {
        // Extrait le nombre du montant (ex: "500€" -> 500)
        const amountMatch = fmdData.amount.match(/[\d,.]+/);
        if (amountMatch) {
          this.discount_value = parseFloat(amountMatch[0].replace(',', '.')) || 0;
          this.discount_type = 'fixed'; // Toujours en montant fixe depuis le formulaire
        }
      }
    }

    this.mode = this.getAttribute('data-mode') || 'full';
    this.selectedDuration = 36; // Durée par défaut (36 mois)
    this.prices = {};
    this.durations = [];

    // Éléments DOM (non utilisés en mode compact)
    this.optionsContainer = this.querySelector('.greenleaze-rental__options');
    this.priceElement = this.querySelector('[data-greenleaze-price]');

    // État de la relecture bornée. Réinitialisé ici car connectedCallback rejoue quand
    // l'élément est déplacé dans le DOM (grille rerendue par les filtres, infinite scroll).
    this.cancelPriceRetry();
    this._retryCount = 0;

    this.setupEventListeners();
    this.initializePrices();
  }

  // Symétrique de connectedCallback : sans ce nettoyage, chaque changement de filtre
  // (theme.js remplace l'innerHTML de la grille) et chaque batch d'infinite scroll
  // laissait autant de listeners window vivants, retenant des nœuds DOM détachés.
  disconnectedCallback() {
    if (this._onPriceActualize) {
      window.removeEventListener('greenleazePriceActualize', this._onPriceActualize);
      this._onPriceActualize = null;
    }
    // Le timer de relecture ne survit jamais à l'élément : le panneau de recherche
    // prédictive remplace son innerHTML à chaque frappe, la grille au filtrage.
    this.cancelPriceRetry();
  }

  setupEventListeners() {
    // Référence stable : permet le retrait dans disconnectedCallback.
    // Retrait défensif d'abord, au cas où connectedCallback rejouerait sans déconnexion.
    if (this._onPriceActualize) {
      window.removeEventListener('greenleazePriceActualize', this._onPriceActualize);
    }
    this._onPriceActualize = () => {
      this.updatePrices();
    };
    // Écouter l'événement de mise à jour des prix de Greenleaze
    window.addEventListener('greenleazePriceActualize', this._onPriceActualize);

    // Si le script Greenleaze est déjà initialisé
    if (
      typeof window.init !== 'undefined' &&
      window.init === true &&
      typeof window.initPrice !== 'undefined' &&
      window.initPrice === true
    ) {
      this.updatePrices();
    }
  }

  initializePrices() {
    // Vérifier si le script Greenleaze est déjà chargé
    if (typeof window.getAllPricesForProduct !== 'function') {
      // Charger le script Greenleaze s'il n'est pas déjà chargé
      if (!window.greenleazeScript) {
        window.greenleazeScript = true;
        const script = document.createElement('script');
        script.src = this.scriptUrl;
        // updatePrices() refait lui-même le contrôle de disponibilité.
        script.onload = () => {
          this.updatePrices();
        };
        document.head.appendChild(script);
      }
      // Un frère a pu injecter le script avant nous : il n'y a alors ni onload pour
      // cette instance, ni event à attendre s'il est déjà passé (anti-pattern n°13).
      // La relecture bornée couvre ces deux cas, et l'échec de chargement du script.
      this.schedulePriceRetry();
      return;
    }
    // Si le script est déjà chargé, mettre à jour les prix immédiatement
    this.updatePrices();
  }

  updatePrices() {
    // Relecture bornée plutôt qu'abandon définitif : le script tiers peut être encore en
    // vol, ou le proxy muet (table de règles vide). Sans cela le nœud reste vide pour
    // toujours, et en parcours FMD la carte n'affiche alors aucun prix ni message.
    if (!this.hasGreenleazePrices()) {
      this.schedulePriceRetry();
      return;
    }
    this.cancelPriceRetry();

    const priceHT = this.productPrice / 100;

    // Mode compact : seule la durée 36 est affichée (cf. updateCompactDisplay).
    // getAllPricesForProduct calcule les 4 durées de monthValues et en jetterait 3 :
    // sur une grille de collection (39 cartes) cela quadruple le coût pour rien.
    if (this.mode === 'compact') {
      this.prices = this.getCompactPrices(priceHT);
      this.updateCompactDisplay();
      return;
    }

    this.prices = window.getAllPricesForProduct(priceHT);
    this.durations = window.monthValues;

    if (window.greenleazeDebug) console.log(this, this.prices, this.durations);

    this.renderDurationButtons();
    this.updatePriceDisplay();
  }

  // Durée unique affichée en mode compact, source de vérité partagée
  // entre getCompactPrices et updateCompactDisplay.
  get compactDuration() {
    return 36;
  }

  // Borne de la relecture : 200 ms x 25 = 5 s au plus, puis libellé de repli.
  // Getters plutôt que constantes de module : ce fichier est inclus par plusieurs
  // sections à la fois, on n'y ajoute pas d'identifiant global.
  get priceRetryDelay() {
    return 200;
  }

  get priceRetryMax() {
    return 25;
  }

  get priceUnavailableLabel() {
    return 'Loyer sur demande';
  }

  // Tester getAllPricesForProduct ne suffit PAS : greenleaze-script.js déclare ses
  // fonctions au top-level d'un script classique, donc elles existent sur window même
  // quand /apps/greenleaze-proxy/pricing a échoué - elles renvoient alors 0 pour toutes
  // les durées, sur une table `priceRules` restée vide. C'est cette table qui fait foi.
  hasGreenleazePrices() {
    return (
      typeof window.getAllPricesForProduct === 'function' &&
      Array.isArray(window.priceRules) &&
      window.priceRules.length > 0 &&
      Array.isArray(window.monthValues) &&
      window.monthValues.length > 0
    );
  }

  // Relit l'état publié par le script tiers au lieu de s'arrêter au premier échec
  // (anti-patterns n°13 et n°23). Aucune requête réseau n'est émise ici : on ne fait
  // que relire des globales, donc le nombre d'appels au proxy reste inchangé.
  schedulePriceRetry() {
    if (this._retryTimer) return;
    if (this._retryCount >= this.priceRetryMax) {
      this.renderPriceUnavailable();
      return;
    }
    this._retryTimer = window.setTimeout(() => {
      this._retryTimer = null;
      this._retryCount += 1;
      this.updatePrices();
    }, this.priceRetryDelay);
  }

  cancelPriceRetry() {
    if (this._retryTimer) {
      window.clearTimeout(this._retryTimer);
      this._retryTimer = null;
    }
  }

  // Fin de la relecture bornée : plutôt qu'une ligne muette, on dit que le loyer n'a
  // pas pu être calculé. Aucun montant n'est inventé : le metafield
  // lld.mensualite_36_mois diverge de Greenleaze, qui fait foi (anti-pattern n°13).
  renderPriceUnavailable() {
    if (this.mode === 'compact') {
      this.textContent = this.priceUnavailableLabel;
      return;
    }
    if (!this.priceElement) return;
    this.priceElement.textContent = this.priceUnavailableLabel;
    if (this.priceElement.nextElementSibling) {
      this.priceElement.nextElementSibling.style.display = 'none';
    }
  }

  getCompactPrices(priceHT) {
    const duration = this.compactDuration;
    // getRuleByProductPriceAndDuration retombe silencieusement sur la dernière durée
    // disponible si celle demandée est absente de monthValues : on afficherait alors
    // la mensualité d'une AUTRE durée sous la clé 36. On ne calcule que si 36 existe
    // réellement — sinon on rend {} et updateCompactDisplay laisse la ligne vide.
    if (!Array.isArray(window.monthValues) || !window.monthValues.includes(duration)) {
      return {};
    }
    return {
      [duration]: window.getRuleByProductPriceAndDuration(priceHT, duration),
    };
  }

  updateCompactDisplay() {
    const priceData = this.prices[this.compactDuration];
    const monthlyPrice = priceData ? priceData.monthly : 0;
    // Jamais de display:none ici : la hauteur est réservée en CSS (min-height: 1em)
    // pour que l'arrivée du prix ne change pas la hauteur de la carte. On n'arrive ici
    // qu'avec une table de règles chargée (cf. hasGreenleazePrices) : un 0 signifie donc
    // qu'aucune règle ne couvre ce prix, pas une panne - la ligne reste vide, comme avant.
    if (monthlyPrice <= 0) {
      this.textContent = '';
      return;
    }
    this.textContent = `dès ${Math.round(monthlyPrice)}€/mois`;
  }

  renderDurationButtons() {
    if (!this.optionsContainer) return;

    this.optionsContainer.innerHTML = '';

    this.durations.forEach((duration) => {
      const button = document.createElement('button');
      button.classList.add('greenleaze-rental__button');
      button.textContent = duration;

      if (duration === this.selectedDuration) {
        button.classList.add('greenleaze-rental__button--selected');
      }

      button.addEventListener('click', () => this.selectDuration(duration));
      this.optionsContainer.appendChild(button);
    });
  }

  selectDuration(duration) {
    this.selectedDuration = duration;

    // Mettre à jour la classe sélectionnée sur les boutons
    const buttons = this.querySelectorAll('.greenleaze-rental__button');
    buttons.forEach((button) => {
      if (parseInt(button.textContent) === duration) {
        button.classList.add('greenleaze-rental__button--selected');
      } else {
        button.classList.remove('greenleaze-rental__button--selected');
      }
    });

    this.updatePriceDisplay();
  }

  /**
   * Calcule le prix mensuel pour une durée spécifique avec le discount appliqué
   * @param {number} duration - La durée en mois
   * @returns {number} Prix mensuel après discount pour cette durée
   */
  calculateMonthlyPriceForDuration(duration) {
    if (!this.prices[duration]) return 0;

    const priceData = this.prices[duration];
    let totalPrice = parseFloat(priceData.monthly);

    if (window.greenleazeDebug)
      console.log(
        'Calculating price for duration:',
        duration,
        'Base total price:',
        totalPrice,
        'Discount type:',
        this.discount_type,
        'Discount value:',
        this.discount_value,
      );

    // Appliquer le discount si différent de 0
    if (this.discount_value !== 0) {
      let monthlyDiscount = 0;
      if (this.discount_type === 'percentage') {
        monthlyDiscount = totalPrice * (this.discount_value / 100);
      } else if (this.discount_type === 'fixed') {
        monthlyDiscount = this.discount_value / 12;
      }
      if (this.fmdCap > 0) {
        monthlyDiscount = Math.min(monthlyDiscount, this.fmdCap);
      }
      totalPrice = Math.max(0, totalPrice - monthlyDiscount);
    }

    if (window.greenleazeDebug) console.log('Final total price after discount:', totalPrice);
    return totalPrice;
  }

  /**
   * Calcule le prix mensuel final en appliquant le discount sur le total
   * Utilise la méthode commune calculateMonthlyPriceForDuration
   * @returns {number} Prix mensuel après discount
   */
  calculateFinalMonthlyPrice() {
    return this.calculateMonthlyPriceForDuration(this.selectedDuration);
  }

  updatePriceDisplay() {
    if (!this.priceElement) return;

    if (window.greenleazeDebug) console.log('this.prices : ', this.prices);

    if (this.prices[this.selectedDuration]) {
      const finalMonthlyPrice = this.reductionFmd
        ? this.prices[this.selectedDuration].monthly
        : this.calculateFinalMonthlyPrice();
      if (window.greenleazeDebug) console.log('Updating price display to:', finalMonthlyPrice.toFixed(0).replace('.', ','));
      this.priceElement.textContent = `${finalMonthlyPrice.toFixed(0).replace('.', ',')} €`;

      if (finalMonthlyPrice === 0) {
        this.priceElement.nextElementSibling.style.display = 'none';
      } else {
        this.priceElement.nextElementSibling.style.display = '';
      }
    }
  }

  /**
   * Récupère les données FMD depuis localStorage
   * @returns {Object|null} Les données FMD ou null si non présentes
   */
  getFmdDataFromSession() {
    try {
      if (window.MintFmdData && typeof window.MintFmdData.getStatus === 'function') {
        const status = window.MintFmdData.getStatus();
        if (status && status.isValid) {
          return status.data || null;
        }
        return null;
      }

      const stored = localStorage.getItem('fmd-data');
      if (!stored) return null;

      const parsed = JSON.parse(stored);
      if (parsed && parsed.timestamp && parsed.expiresIn) {
        const isExpired = Date.now() - parsed.timestamp > parsed.expiresIn;
        if (isExpired) {
          localStorage.removeItem('fmd-data');
          return null;
        }
        return parsed.data || null;
      }

      const legacyData = parsed && parsed.data ? parsed.data : parsed;
      if (legacyData && typeof legacyData === 'object') {
        const migrated = {
          data: legacyData,
          timestamp: Date.now(),
          expiresIn: 7 * 24 * 60 * 60 * 1000,
        };
        localStorage.setItem('fmd-data', JSON.stringify(migrated));
        return legacyData;
      }

      return null;
    } catch (e) {
      console.error('GreenleazeRentalOptions: Error reading fmd-data from localStorage', e);
      return null;
    }
  }
}

customElements.define('greenleaze-rental-options', GreenleazeRentalOptions);
})();
